import {_m} from 'INTL';

export enum EBankWithdrawalOptions {
  BANK_INSTANT_WITHDRAWAL = 'bank_instant_withdrawal',
  BANK_DEFAULT_WITHDRAWAL = 'bank_default_withdrawal',
}

/**
 * BankWithdrawalConfig is used to configure the bank withdrawal flow.
 * It contains the withdrawal type and whether to launch the bank link on mount (for Bank Withdrawal popup case).
 */
export type BankWithdrawalConfig = {
  withdrawalType: EBankWithdrawalOptions;
  launchBankLinkOnMount?: boolean;
  launchFromStandardWithdrawalPage?: boolean;
};

// MODALS
export const WITHDRAWAL_SUCCESS_MODAL_TEST_IDS = {
  MAIN_CONTAINER: 'withdrawal_success_modal',
  IMAGE: 'withdrawal_success_modal_check_icon',
  TITLE: 'withdrawal_success_modal_title',
  AMOUNT_LABEL: 'withdrawal_success_modal_amount_label',
  AMOUNT_VALUE: 'withdrawal_success_modal_amount_value',
  BONUS_CASH_LABEL: 'withdrawal_success_modal_bonus_cash_label',
  BONUS_CASH_VALUE: 'withdrawal_success_modal_bonus_cash_value',
  PROMO_BONUS_CASH_LABEL: 'withdrawal_success_modal_promo_bonus_cash_label',
  PROMO_BONUS_CASH_VALUE: 'withdrawal_success_modal_promo_bonus_cash_value',
  TOTAL_LABEL: 'withdrawal_success_modal_total_label',
  TOTAL_VALUE: 'withdrawal_success_modal_total_value',
  SUBTEXT: 'withdrawal_success_modal_subtext',
  PRIMARY_BUTTON: 'withdrawal_success_modal_ok',
  CHECKBOX: 'withdrawal_success_modal_checkbox',
  CHECKBOX_COPY: 'withdrawal_success_modal_checkbox_copy',
  BODY: 'withdrawal_success_modal_body',
};

export const WITHDRAWAL_ERROR_MODAL_TEST_IDS = {
  MAIN_CONTAINER: 'withdrawal_error_modal',
  IMAGE: 'withdrawal_error_modal_check_icon',
  TITLE: 'withdrawal_error_modal_title',
  AMOUNT_LABEL: 'withdrawal_error_modal_amount_label',
  AMOUNT_VALUE: 'withdrawal_error_modal_amount_value',
  BONUS_CASH_LABEL: 'withdrawal_error_modal_bonus_cash_label',
  BONUS_CASH_VALUE: 'withdrawal_error_modal_bonus_cash_value',
  PROMO_BONUS_CASH_LABEL: 'withdrawal_error_modal_promo_bonus_cash_label',
  PROMO_BONUS_CASH_VALUE: 'withdrawal_error_modal_promo_bonus_cash_value',
  TOTAL_LABEL: 'withdrawal_error_modal_total_label',
  TOTAL_VALUE: 'withdrawal_error_modal_total_value',
  SUBTEXT: 'withdrawal_error_modal_subtext',
  PRIMARY_BUTTON: 'withdrawal_error_modal_ok',
  CHECKBOX: 'withdrawal_error_modal_checkbox',
  CHECKBOX_COPY: 'withdrawal_error_modal_checkbox_copy',
  BODY: 'withdrawal_error_modal_body',
};

export const WITHDRAWAL_INFO_MODAL_TEST_IDS = {
  MAIN_CONTAINER: 'withdrawal_info_modal',
  IMAGE: 'withdrawal_info_modal_check_icon',
  TITLE: 'withdrawal_info_modal_title',
  AMOUNT_LABEL: 'withdrawal_info_modal_amount_label',
  AMOUNT_VALUE: 'withdrawal_info_modal_amount_value',
  BONUS_CASH_LABEL: 'withdrawal_info_modal_bonus_cash_label',
  BONUS_CASH_VALUE: 'withdrawal_info_modal_bonus_cash_value',
  PROMO_BONUS_CASH_LABEL: 'withdrawal_info_modal_promo_bonus_cash_label',
  PROMO_BONUS_CASH_VALUE: 'withdrawal_info_modal_promo_bonus_cash_value',
  TOTAL_LABEL: 'withdrawal_info_modal_total_label',
  TOTAL_VALUE: 'withdrawal_info_modal_total_value',
  SUBTEXT: 'withdrawal_info_modal_subtext',
  PRIMARY_BUTTON: 'withdrawal_info_modal_ok',
  CHECKBOX: 'withdrawal_info_modal_checkbox',
  CHECKBOX_COPY: 'withdrawal_info_modal_checkbox_copy',
  BODY: 'withdrawal_info_modal_body',
};

export const WITHDRAWAL_INFO_MODAL_DEFAULTS = {
  title: _m('Withdrawal Info'),
  buttonTextPrimary: _m('Ok'),
  icon: 'ic_info_skz.png',
};

export const WITHDRAWAL_SUCCESS_MODAL_DEFAULTS = {
  title: _m('Withdrawal Successful'),
  buttonTextPrimary: _m('Ok'),
  icon: 'icon_bank_withdrawal_success.svg',
};

export const WITHDRAWAL_ERROR_MODAL_DEFAULTS = {
  title: _m('Withdrawal Error'),
  buttonTextPrimary: _m('Ok'),
  icon: 'icon_x_gradient_background.svg',
};

export const WITHDRAWAL_CONFIRMATION_MODAL_DEFAULTS = {
  title: _m('Confirm Withdrawal'),
  buttonTextPrimary: _m('Withdraw'),
  buttonTextSecondary: _m('Cancel'),
};

export const BANK_WITHDRAWAL_SUCCESS_MODAL_DEFAULTS = {
  title: _m('Withdrawal Successful'),
  buttonTextPrimary: _m('Ok'),
  icon: 'icon_bank_withdrawal_success.svg',
  bodyText: (amount: string) =>
    _m(
      `Your withdrawal of \${0} request has been completed. You will receive an email confirming the transaction.`,
      parseFloat(amount).toFixed(2)
    ),
};

export const BANK_WITHDRAWAL_SUCCESS_MODAL_RTP_DEFAULTS = {
  title: _m('Withdrawal Successful'),
  buttonTextPrimary: _m('Ok'),
  icon: 'icon_withdrawal_option_instant_banking.png',
  bodyText: (amount: string) =>
    _m(
      `Your instant withdrawal request of \${0} has been completed. You will receive an email confirming the transaction.`,
      parseFloat(amount).toFixed(2)
    ),
};

export const BANK_WITHDRAWAL_ERROR_MODAL_DEFAULTS = {
  title: _m('Withdrawal Error'),
  bodyText: _m(
    'Your withdrawal request has encountered an error.\n\nPlease try again or contact support.'
  ),
};

// ACCOUNT DETAILS
export const BONUS_CASH_INFO_TEXT = _m(
  'Bonus cash cannot be withdrawn.\nAll Bonus cash will be forfeited\nwhen you withdraw.'
);
export const BONUS_CASH_INFO_TITLE = _m('Bonus Cash');

export const UNSETTLED_BALANCE_INFO_TEXT = _m('Your Unsettled Balance\nis not settled yet.');
export const UNSETTLED_BALANCE_INFO_TITLE = _m('Unsettled Balance');

export const AEROPAY_FAQ_URL =
  'https://support.skillz.com/hc/en-us/articles/37471978518676-Skillz-Aeropay-Secure-Bank-Transfers-Made-Easy';

export const PLAYTHROUGH_FAQ_URL =
  'https://support.skillz.com/hc/en-us/articles/203685739-How-do-withdrawals-work-Will-I-[…]y-withdrawal-via-check-or-online-Can-I-cancel-my-withdrawal';

// LINK BANK BUTTON
export const LINK_BANK_BUTTON_TEXT = _m('Link Your Bank Now!');

// WITHDRAWAL INPUT
export const WITHDRAWAL_INPUT_PLACEHOLDER = _m('Enter Withdrawal Amount');
export const WITHDRAWAL_INPUT_LABEL = _m('Withdrawal Amount');

// WITHDRAWAL OPTIONS

// TODO: update icon and text according to designs
// TODO: change this to a record without causing issues in the tests
export const withdrawalOptionMapping = () => {
  return {
    [EBankWithdrawalOptions.BANK_INSTANT_WITHDRAWAL]: {
      title: _m('Instant Bank Withdrawals'),
      subTitle: _m('Fast, Secure, and hassle-free'),
      icon: 'icon_withdrawal_option_instant_banking.png',
    },
    [EBankWithdrawalOptions.BANK_DEFAULT_WITHDRAWAL]: {
      title: _m('Fast Bank Withdrawal'),
      subTitle: _m('Fast, Secure, and hassle-free'),
      icon: 'icon_withdrawal_option_fast_bank.svg',
    },
  };
};

// WITHDRAW BUTTON
export const WITHDRAW_BUTTON_TEXT = _m('WITHDRAW NOW');
